import React, { useState, useEffect } from 'react';
import { Form, Button } from 'react-bootstrap';
import axios from 'axios';
import '../assets/styles/JoinPage.css';
import { createEmployee } from '../api/api';
import { API_CONFIG } from '../config/config';

const EmployeeForm = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    gender: '',
    phoneNumber: '',
    emailAddress: '',
    education: '',
    dateOfBirth: '',
    aadhar: '',
    latitude: 0,
    lognitude: 0,
    skills: [],
    languages: [],
    active: 'Y',
  });

  const [skillsOptions, setSkillsOptions] = useState([]);
  const [languagesOptions, setLanguagesOptions] = useState([]);
  const [responseMessage, setResponseMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch skills and languages on component load
  useEffect(() => {
    const fetchSkillsAndLanguages = async () => {
      try {
        const [skillsResponse, languagesResponse] = await Promise.all([
          axios.get(`${API_CONFIG.endpoint}/api/leads/skills`),
          axios.get(`${API_CONFIG.endpoint}/api/leads/languages`),
        ]);
        setSkillsOptions(skillsResponse.data);
        setLanguagesOptions(languagesResponse.data);
      } catch (error) {
        console.error('Error fetching skills or languages:', error.message);
      }
    };
    fetchSkillsAndLanguages();
  }, []);

  // Handle form changes for text inputs
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Handle multi-select changes for checkboxes
  const handleCheckboxChange = (name, value) => {
    const selectedOptions = formData[name];
    if (selectedOptions.includes(value)) {
      setFormData({
        ...formData,
        [name]: selectedOptions.filter((option) => option !== value),
      });
    } else {
      setFormData({
        ...formData,
        [name]: [...selectedOptions, value],
      });
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setResponseMessage(null);
    setErrorMessage(null);

    try {
      await createEmployee(formData);
      setResponseMessage('Profile created successfully!');
      setFormData({
        firstName: '',
        lastName: '',
        gender: '',
        phoneNumber: '',
        emailAddress: '',
        education: '',
        dateOfBirth: '',
        aadhar: '',
        latitude: 0,
        lognitude: 0,
        skills: [],
        languages: [],
        active: 'Y',
      });
    } catch (error) {
      setErrorMessage(error.response?.data?.message || 'An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form className="employee-form" onSubmit={handleSubmit}>
      <h2>Create Employee Profile</h2>
      {responseMessage && <p className="success-message">{responseMessage}</p>}
      {errorMessage && <p className="error-message">{errorMessage}</p>}

      <Form.Group>
        <Form.Label>First Name</Form.Label>
        <Form.Control
          type="text"
          name="firstName"
          value={formData.firstName}
          onChange={handleChange}
          placeholder="Enter first name"
          required
          disabled={isSubmitting}
        />
      </Form.Group>

      <Form.Group>
        <Form.Label>Last Name</Form.Label>
        <Form.Control
          type="text"
          name="lastName"
          value={formData.lastName}
          onChange={handleChange}
          placeholder="Enter last name"
          required
          disabled={isSubmitting}
        />
      </Form.Group>

      <Form.Group>
        <Form.Label>Gender</Form.Label>
        <Form.Control
          type="text"
          name="gender"
          value={formData.gender}
          onChange={handleChange}
          placeholder="Enter gender"
          required
          disabled={isSubmitting}
        />
      </Form.Group>

      <Form.Group>
        <Form.Label>Date of Birth</Form.Label>
        <Form.Control
          type="date"
          name="dateOfBirth"
          value={formData.dateOfBirth}
          onChange={handleChange}
          required
          disabled={isSubmitting}
        />
      </Form.Group>

      <Form.Group>
        <Form.Label>Education</Form.Label>
        <Form.Control
          type="text"
          name="education"
          value={formData.education}
          onChange={handleChange}
          placeholder="Enter education"
          required
          disabled={isSubmitting}
        />
      </Form.Group>

      <Form.Group>
        <Form.Label>Phone Number</Form.Label>
        <Form.Control
          type="text"
          name="phoneNumber"
          value={formData.phoneNumber}
          onChange={handleChange}
          placeholder="Enter phone number"
          required
          disabled={isSubmitting}
        />
      </Form.Group>

      <Form.Group>
        <Form.Label>Email Address</Form.Label>
        <Form.Control
          type="email"
          name="emailAddress"
          value={formData.emailAddress}
          onChange={handleChange}
          placeholder="Enter email address"
          required
          disabled={isSubmitting}
        />
      </Form.Group>

      <Form.Group>
        <Form.Label>Aadhar Number</Form.Label>
        <Form.Control
          type="text"
          name="aadhar"
          value={formData.aadhar}
          onChange={handleChange}
          placeholder="Enter Aadhar number"
          required
          disabled={isSubmitting}
        />
      </Form.Group>

      <Form.Group>
        <Form.Label>Skills</Form.Label>
        {skillsOptions.map((skill) => (
          <Form.Check
            key={skill.id}
            type="checkbox"
            label={skill.name}
            value={skill.name}
            checked={formData.skills.includes(skill.name)}
            onChange={() => handleCheckboxChange('skills', skill.name)}
            disabled={isSubmitting}
          />
        ))}
      </Form.Group>

      <Form.Group>
        <Form.Label>Languages</Form.Label>
        {languagesOptions.map((lang) => (
          <Form.Check
            key={lang.id}
            type="checkbox"
            label={lang.name}
            value={lang.name}
            checked={formData.languages.includes(lang.name)}
            onChange={() => handleCheckboxChange('languages', lang.name)}
            disabled={isSubmitting}
          />
        ))}
      </Form.Group>

      <Button variant="primary" type="submit" className="submit-button" disabled={isSubmitting}>
        {isSubmitting ? 'Submitting...' : 'Create Profile'}
      </Button>
    </Form>
  );
};

export default EmployeeForm;
